import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";

import {
  AlternateIcon,
  DatasetWidgetIcon,
  ExpandCollapseIcon,
  PolygonIcon
} from "@/assets/icons";
import AnalysisJobListMapMenuControl from "@/components/spatialAnalysis/analysisJobList/analysisJobListControl";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { cn } from "@/lib/utils";
import { RootState } from "@/store";
import {
  setAreaInput,
  setCatchmentSettingsTemp,
  setPolygonSettingsTemp
} from "@/store/slice/spatialSlice";
import { enableDrawPolygon } from "@/store/slice/uiSlice";
import { AreaInputType, ECatchmentInputBy } from "@/types/spatial";

import { twMerge } from "tailwind-merge";

import MapControlCluster from "../controls/MapControlCluster";
import MapFullScreenControl from "../controls/MapFullScreenControl";
import MapRecenterControl from "../controls/MapRecenterControl";
import MapUserLocationControl from "../controls/MapUserLocationControl";
import MapZoomControl from "../controls/MapZoomControl";
import MapMenuItemControl from "../menuItemControl";
import BaseMapMenu from "./BaseMapMenu";

type Props = {
  zoomLevel: number;
  isLegendVisible: boolean;
  handleMapFullScreen: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onToggleLegendVisibility: () => void;
  onToggleLegendVisualization: () => void;
  onToggleAnalysisJobList: () => void;
  onToggleDatasetWidget: () => void;
  isDatasetWidgetVisible?: boolean;
};

const MapMenuControl = ({
  zoomLevel,
  isLegendVisible,
  handleMapFullScreen,
  onZoomIn,
  onZoomOut,
  onToggleLegendVisibility,
  onToggleLegendVisualization,
  onToggleAnalysisJobList,
  onToggleDatasetWidget,
  isDatasetWidgetVisible
}: Props) => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const isPreviewMode = searchParams.get("preview") === "true";
  const {
    enterDrawMode,
    enterSelectMode,
    synchronizeFeatures,
    resetMapView,
    locateUser,
    isLocatingUser,
    isUserLocationBlocked
  } = useMap();
  const { isDrawPolygon, showAnalysisResultPopup, showAnalysisSummaryPopup } =
    useSelector((state: RootState) => state.ui);
  const { polygonSettingsTemp, polygonSettings, latestAnalysisId } =
    useSelector((state: RootState) => state.spatial);
  const {
    isDrawPolygonEnabled,
    isAnalysisJobListEnabled,
    isDatasetWidgetEnabled,
    isBVTDataForCatchmentEnabled,
    isUserGeolocationEnabled
  } = useAppFeatures();

  const onOpenDrawPolygon = () => {
    if (isDrawPolygon) return;

    const data = {
      value: isBVTDataForCatchmentEnabled
        ? AreaInputType.CATCHMENT
        : AreaInputType.POLYGON,
      label: isBVTDataForCatchmentEnabled ? "Catchment" : "Polygon"
    };

    dispatch(setAreaInput(data));
    if (isBVTDataForCatchmentEnabled) {
      dispatch(
        setCatchmentSettingsTemp({
          catchmentInputBy: ECatchmentInputBy.BY_POLYGON
        })
      );
    }
    dispatch(enableDrawPolygon());
    synchronizeFeatures();
    dispatch(setPolygonSettingsTemp(polygonSettings));

    if (Object.keys(polygonSettingsTemp || {}).length < 3) {
      enterDrawMode();
    } else {
      enterSelectMode();
    }
  };

  return (
    <>
      {/* Top right menu */}
      {isAnalysisJobListEnabled && (
        <AnalysisJobListMapMenuControl
          onToggleAnalysisJobList={onToggleAnalysisJobList}
        />
      )}
      {isDatasetWidgetEnabled && !latestAnalysisId && !isPreviewMode && (
        <MapMenuItemControl
          className={twMerge(
            "absolute top-20 right-6 z-[1] rounded-lg border-[1px] border-gainsboro",
            isDatasetWidgetVisible && "border-teal-500"
          )}
          onClick={onToggleDatasetWidget}
          aria-label="Widget Summary"
          tooltipContent="Widget Summary"
        >
          <DatasetWidgetIcon
            className={twMerge(
              "fill-dim-gray",
              isDatasetWidgetVisible && "fill-teal-500"
            )}
          />
        </MapMenuItemControl>
      )}
      {/* Bottom right menu */}
      <MapControlCluster
        data-testid="bottom-right-controls"
        data-tourid="map-tools-control"
      >
        {isUserGeolocationEnabled && (
          <MapUserLocationControl
            className="mb-2"
            onLocate={locateUser}
            isLocating={isLocatingUser}
            isBlocked={isUserLocationBlocked}
          />
        )}
        <MapRecenterControl className="mb-2" onRecenter={resetMapView} />
        {isDrawPolygonEnabled &&
          !showAnalysisResultPopup &&
          !showAnalysisSummaryPopup &&
          !isPreviewMode && (
            <MapMenuItemControl
              className={twMerge(
                "rounded-lg border-[1px] border-gainsboro my-2",
                isDrawPolygon && "border-teal-500"
              )}
              onClick={onOpenDrawPolygon}
              aria-label="Draw Polygon"
              tooltipContent="Draw a custom polygon on the map."
            >
              <PolygonIcon
                className={twMerge(
                  "fill-dim-gray",
                  isDrawPolygon && "fill-teal-500"
                )}
              />
            </MapMenuItemControl>
          )}
        <MapMenuItemControl
          className={twMerge(
            "rounded-lg border-[1px] border-gainsboro my-2",
            !isLegendVisible && "hidden"
          )}
          onClick={onToggleLegendVisualization}
          tooltipContent="Switch to dual map view."
        >
          <ExpandCollapseIcon className="fill-dim-gray" />
        </MapMenuItemControl>
        <MapFullScreenControl onToggle={handleMapFullScreen} />
        <MapZoomControl
          zoomLevel={zoomLevel}
          onZoomIn={onZoomIn}
          onZoomOut={onZoomOut}
        />
        <MapMenuItemControl
          className={cn(
            "rounded-lg border-[1px] border-gainsboro",
            isLegendVisible && "border-teal-500"
          )}
          onClick={onToggleLegendVisibility}
        >
          <AlternateIcon className="fill-dim-gray" />
        </MapMenuItemControl>

        <BaseMapMenu />
      </MapControlCluster>
    </>
  );
};

export default MapMenuControl;
